'use client';

import { useEffect } from 'react';

/**
 * Locks the page viewport (disables user zoom / scaling) for the lifetime of the
 * mounting component by injecting or mutating the <meta name="viewport"> tag
 * with non‑scalable settings. On unmount, restores the previous viewport meta
 * content or a sane scalable fallback, preserving accessibility elsewhere.
 *
 * Use sparingly: preventing zoom can impact low‑vision users who rely on pinch
 * or gesture zoom. Restrict usage to tightly controlled flows (e.g. device
 * diagnostic steps requiring stable layout / coordinates) and keep the locked
 * period as short as possible.
 *
 */
export function useViewportZoomLock() {
  useEffect(() => {
    const LOCKED =
      'width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no';
    const NORMAL = 'width=device-width, initial-scale=1.0';

    const manageViewport = () => {
      let viewportEl = document.querySelector<HTMLMetaElement>(
        'meta[name="viewport"]'
      );

      if (!viewportEl) {
        viewportEl = document.createElement('meta');
        viewportEl.name = 'viewport';
        viewportEl.content = LOCKED;
        document.head.appendChild(viewportEl);

        return () => {
          viewportEl?.setAttribute('content', NORMAL);
        };
      }

      const previousContent = viewportEl.getAttribute('content');
      viewportEl.setAttribute('content', LOCKED);

      return () => {
        viewportEl?.setAttribute('content', previousContent || NORMAL);
      };
    };

    const cleanup = manageViewport();

    return cleanup;
  }, []);
}
